import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Plus, Calendar, FileText, ChevronRight, Copy,
  Loader2, Check, Trash2, Zap, MessageSquare, X, ArrowRight,
  Link2, LayoutTemplate, Briefcase, Linkedin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { templateIcons } from '../data/templateIcons';
import { API_URL, cn } from '../lib/utils';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sprints, setSprints] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showQuizTemplates, setShowQuizTemplates] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCommentGenerator, setShowCommentGenerator] = useState(false);
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [commentTone, setCommentTone] = useState('Professional');
  const [generatedComments, setGeneratedComments] = useState([]);
  const [generatingComments, setGeneratingComments] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [linkedInPostUrl, setLinkedInPostUrl] = useState('');

  useEffect(() => {
    fetchSprints();
    fetchQuizzes();
    fetchTemplates();
  }, []);

  const fetchSprints = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sprints`, { credentials: 'include' });
      if (response.ok) setSprints(await response.json());
    } catch (error) {
      console.error('Error fetching sprints:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quizzes`, { credentials: 'include' });
      if (response.ok) setQuizzes(await response.json());
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates`, { credentials: 'include' });
      if (response.ok) setTemplates(await response.json());
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const deleteSprint = async (sprintId) => {
    if (!window.confirm('Delete this sprint?')) return;
    try {
      const response = await fetch(`${API_URL}/api/sprints/${sprintId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) setSprints(sprints.filter(s => s.sprint_id !== sprintId));
    } catch (error) {
      console.error('Error deleting sprint:', error);
    }
  };

  const deleteQuiz = async (quizId) => {
    if (!window.confirm('Delete this quiz?')) return;
    try {
      const response = await fetch(`${API_URL}/api/quizzes/${quizId}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) setQuizzes(quizzes.filter(q => q.quiz_id !== quizId));
    } catch (error) {
      console.error('Error deleting quiz:', error);
    }
  };

  const handleTemplateSelect = (template) => {
    setShowTemplates(false);
    navigate('/create', { state: { template } });
  };

  const handleQuizTemplateSelect = (template) => {
    setShowQuizTemplates(false);
    navigate('/create-quiz', { state: { template } });
  };

  const generateComments = async () => {
    if (!linkedInPostUrl.trim()) {
      alert('Please enter the LinkedIn post URL');
      return;
    }
    
    // Validate LinkedIn URL
    const linkedInPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/(posts|feed|pulse)\/.+/i;
    if (!linkedInPattern.test(linkedInPostUrl.trim())) {
      alert('Please enter a valid LinkedIn post URL (e.g., https://linkedin.com/posts/...)');
      return;
    }
    
    setGeneratingComments(true);
    setGeneratedComments([]);
    try {
      const response = await fetch(`${API_URL}/api/comments/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ post_url: linkedInPostUrl, tone: commentTone })
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedComments(data.comments || []);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to generate comments');
      }
    } catch {
      alert('Failed to generate comments');
    } finally {
      setGeneratingComments(false);
    }
  };

  const importFromUrl = async () => {
    if (!urlInput.trim()) return;
    
    // Validate URL format
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    if (!urlPattern.test(urlInput.trim())) {
      alert('Please enter a valid URL');
      return;
    }
    
    setUrlLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/import-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url: urlInput })
      });
      if (response.ok) {
        const data = await response.json();
        setShowUrlImport(false);
        setUrlInput('');
        navigate('/create', { state: { importedContent: data } });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to import content from URL');
      }
    } catch {
      alert('Failed to import content');
    } finally {
      setUrlLoading(false);
    }
  };

  const copyComment = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const postCommentToLinkedIn = async (commentText, index) => {
    if (!linkedInPostUrl.trim()) {
      alert('Please enter the LinkedIn post URL first');
      return;
    }
    await navigator.clipboard.writeText(commentText);
    setCopied(`linkedin-${index}`);
    window.open(linkedInPostUrl, '_blank');
    setTimeout(() => setCopied(null), 3000);
  };

  const canCreateSprint = () => {
    if (user?.subscription === 'pro' || user?.subscription === 'premium') return true;
    // Free tier: 1 sprint lifetime
    return (user?.total_sprints_lifetime || 0) < 1;
  };

  const canGenerateComments = () => {
    return user?.subscription === 'pro' || user?.subscription === 'premium';
  };

  const canCreateQuiz = () => {
    return user?.subscription === 'premium';
  };

  const getUsageStats = () => {
    if (user?.subscription === 'premium') {
      return { sprints: '∞', comments: '∞', quizzes: '∞', label: 'Premium - Unlimited Everything' };
    }
    if (user?.subscription === 'pro') {
      return { sprints: '∞', comments: '∞', quizzes: '0', label: 'Pro - Unlimited Sprints & Comments' };
    }
    // Free tier
    const sprintsUsed = user?.total_sprints_lifetime || 0;
    return { sprints: `${sprintsUsed}/1`, comments: '0', quizzes: '0', label: 'Free - 1 Sprint Remaining', sprintsRemaining: 1 - sprintsUsed };
  };

  const usageStats = getUsageStats();
  const categories = ['All', ...new Set(templates.map(t => t.category).filter(Boolean))];
  const filteredTemplates = selectedCategory === 'All' ? templates : templates.filter(t => t.category === selectedCategory);

  return (
    <div className="min-h-screen bg-main">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Usage Stats Widget */}
          {user && (
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-primary/10 to-indigo-500/10 border border-primary/20">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-xs text-muted">Sprints</p>
                      <p className="text-sm font-medium text-foreground">{usageStats.sprints}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-emerald-500" />
                    <div>
                      <p className="text-xs text-muted">Comments</p>
                      <p className="text-sm font-medium text-foreground">{usageStats.comments}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-xs text-muted">Quizzes</p>
                      <p className="text-sm font-medium text-foreground">{usageStats.quizzes}</p>
                    </div>
                  </div>
                </div>
                {user?.subscription === 'free' && usageStats.sprintsRemaining <= 0 && (
                  <button onClick={() => navigate('/subscription')} className="text-sm text-primary hover:underline flex items-center gap-1">
                    Upgrade for Unlimited <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                {user?.subscription === 'free' && usageStats.sprintsRemaining > 0 && (
                  <button onClick={() => navigate('/subscription')} className="text-sm text-primary hover:underline flex items-center gap-1">
                    Upgrade to Pro <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                {user?.subscription === 'pro' && (
                  <button onClick={() => navigate('/subscription')} className="text-sm text-purple-500 hover:underline flex items-center gap-1">
                    Unlock Quizzes with Premium <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Content Tools</h1>
              <p className="text-muted">Create LinkedIn content that builds trust and grows your network.</p>
            </div>
          </div>

          {/* Main Action Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Sprint Generator Card */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-card to-card border border-primary/20 p-6">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">7-Day Content Sprint</h2>
                    <p className="text-sm text-muted">Full week of LinkedIn posts</p>
                  </div>
                </div>
                <p className="text-muted mb-6">Generate a complete 7-day content plan with hooks, posts, CTAs, and A/B variants. Perfect for thought leadership and lead generation.</p>
                
                <div className="space-y-3">
                  <button onClick={() => canCreateSprint() ? setShowTemplates(true) : navigate('/subscription')} className={cn("w-full flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-card-hover border border-border transition-colors text-left", !canCreateSprint() && "opacity-50")}>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <LayoutTemplate className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">Use a Template</p>
                      <p className="text-xs text-muted">15 pre-built templates for different niches</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                  
                  <button onClick={() => canCreateSprint ? setShowUrlImport(true) : navigate('/subscription')} className={cn("w-full flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-card-hover border border-border transition-colors text-left", !canCreateSprint && "opacity-50")}>
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
                      <Link2 className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">Import from URL</p>
                      <p className="text-xs text-muted">Create from LinkedIn post, article, or tweet</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                  
                  <button onClick={() => canCreateSprint ? navigate('/create') : navigate('/subscription')} className={cn("w-full flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-card-hover border border-border transition-colors text-left", !canCreateSprint && "opacity-50")}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <Plus className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">Start from Scratch</p>
                      <p className="text-xs text-muted">Define your own niche and audience</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                </div>
              </div>
            </div>

            {/* Comment Generator Card */}
            <motion.div whileHover={{ scale: canGenerateComments() ? 1.02 : 1 }} className={cn("relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/10 via-card to-card border border-amber-500/20 p-6 cursor-pointer group", !canGenerateComments() && "opacity-50")} onClick={() => canGenerateComments() ? setShowCommentGenerator(true) : navigate('/subscription')}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">Comment Generator</h2>
                    <p className="text-sm text-muted">{canGenerateComments() ? 'Engage meaningfully' : 'Pro & Premium Only'}</p>
                  </div>
                </div>
                <p className="text-muted mb-4">Generate thoughtful, professional comments for any LinkedIn post. Build relationships and grow your network through engagement.</p>
                <div className="flex items-center gap-2 text-sm text-amber-500 group-hover:gap-3 transition-all">
                  <span>{canGenerateComments() ? 'Generate Comments' : 'Upgrade to Unlock'}</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Smart Questionnaire Builder Card */}
          <div className="mb-10">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 via-card to-card border border-emerald-500/20 p-6">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">Smart Questionnaire Builder</h2>
                    <p className="text-sm text-muted">Engage audience, capture leads</p>
                  </div>
                </div>
                <p className="text-muted mb-6">Create interactive quizzes, surveys, or assessments to engage your LinkedIn audience and capture qualified leads with personalized results.</p>
                
                <div className="grid sm:grid-cols-2 gap-3">
                  <button onClick={() => canCreateQuiz() ? setShowQuizTemplates(true) : navigate('/subscription')} className={cn("flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-card-hover border border-border transition-colors text-left", !canCreateQuiz() && "opacity-50")}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <LayoutTemplate className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">Use a Template</p>
                      <p className="text-xs text-muted">{canCreateQuiz() ? 'Pre-built quiz templates by niche' : 'Premium Only'}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                  
                  <button onClick={() => navigate('/create-quiz')} className="flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-card-hover border border-border transition-colors text-left">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                      <Plus className="w-5 h-5 text-teal-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">Start from Scratch</p>
                      <p className="text-xs text-muted">Custom quiz for your audience</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Your Quizzes Section */}
          {quizzes.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Your Quizzes</h2>
              <div className="grid gap-4">
                {quizzes.map((quiz, index) => (
                  <motion.div key={quiz.quiz_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="card group cursor-pointer" onClick={() => quiz.status === 'completed' && navigate(`/quiz/${quiz.quiz_id}`)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-emerald-500" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-foreground">{quiz.name}</h3>
                            <p className="text-sm text-muted">{quiz.niche}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <span className="text-muted">{new Date(quiz.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          <span className="text-muted">{quiz.question_count} questions</span>
                          {quiz.status === 'completed' && <span className="flex items-center gap-1 text-emerald-500"><Check className="w-3 h-3" /> Ready</span>}
                          {quiz.status === 'generating' && <span className="flex items-center gap-1 text-amber-500"><Loader2 className="w-3 h-3 animate-spin" /> Generating...</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); deleteQuiz(quiz.quiz_id); }} className="p-2 text-muted hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-5 h-5 text-muted" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Your Sprints Section */}
          <div className="mb-6">
            <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Your Sprints</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : sprints.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-card flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-muted" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No sprints yet</h3>
              <p className="text-muted text-sm">Use the options above to create your first sprint.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {sprints.map((sprint, index) => (
                <motion.div key={sprint.sprint_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="card group cursor-pointer" onClick={() => sprint.status === 'completed' && navigate(`/sprint/${sprint.sprint_id}`)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-foreground">{sprint.inputs?.userName}'s Sprint</h3>
                          <p className="text-sm text-muted">{sprint.inputs?.niche}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="text-muted">{new Date(sprint.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        {sprint.status === 'completed' && <span className="flex items-center gap-1 text-emerald-500"><Check className="w-3 h-3" /> Ready</span>}
                        {sprint.status === 'generating' && <span className="flex items-center gap-1 text-amber-500"><Loader2 className="w-3 h-3 animate-spin" /> Generating...</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); deleteSprint(sprint.sprint_id); }} className="p-2 text-muted hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {sprint.status === 'completed' && <ChevronRight className="w-5 h-5 text-muted group-hover:text-foreground" />}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTemplates(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Choose a Template</h2>
                  <p className="text-muted text-sm mt-1">{templates.length} templates for different niches</p>
                </div>
                <button onClick={() => setShowTemplates(false)} className="p-2 text-muted hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              
              <div className="px-6 py-3 border-b border-border flex gap-2 overflow-x-auto">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)} className={cn("px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors", selectedCategory === cat ? "bg-primary text-white" : "bg-card-hover text-muted hover:text-foreground")}>{cat}</button>
                ))}
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[55vh]">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredTemplates.map((template) => {
                    const IconComponent = templateIcons[template.icon] || Briefcase;
                    return (
                      <motion.button key={template.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => handleTemplateSelect(template)} className="card text-left hover:border-primary/50 transition-all p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-foreground text-sm">{template.name}</h3>
                            <p className="text-xs text-muted mt-1 line-clamp-2">{template.description}</p>
                            <div className="flex gap-1 mt-2">
                              <span className="px-2 py-0.5 rounded bg-card-hover text-xs text-muted">{template.inputs.tone}</span>
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comment Generator Modal */}
      <AnimatePresence>
        {showCommentGenerator && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowCommentGenerator(false); setGeneratedComments([]); setLinkedInPostUrl(''); }}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-card border border-border rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">Comment Generator</h2>
                    <p className="text-muted text-sm">Generate thoughtful comments for LinkedIn posts</p>
                  </div>
                </div>
                <button onClick={() => { setShowCommentGenerator(false); setGeneratedComments([]); }} className="p-2 text-muted hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">1</div>
                    <label className="text-sm font-medium text-foreground">LinkedIn Post URL</label>
                  </div>
                  <input type="url" value={linkedInPostUrl} onChange={(e) => setLinkedInPostUrl(e.target.value)} placeholder="https://www.linkedin.com/posts/username_activity-123..." className="input w-full text-sm mb-4" data-testid="comment-url-input" />
                  
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-muted">Comment Style:</span>
                    {['Professional', 'Friendly', 'Bold', 'Thoughtful'].map((t) => (
                      <button key={t} onClick={() => setCommentTone(t)} className={cn("px-3 py-1.5 rounded-full text-sm font-medium transition-colors", commentTone === t ? "bg-amber-500 text-white" : "bg-card-hover text-muted hover:text-foreground")} data-testid={`comment-tone-${t.toLowerCase()}`}>{t}</button>
                    ))}
                  </div>
                </div>
                
                <button onClick={generateComments} disabled={generatingComments || !linkedInPostUrl.trim()} className="w-full btn-primary bg-amber-500 hover:bg-amber-600 flex items-center justify-center gap-2 mb-6" data-testid="generate-comments-btn">
                  {generatingComments ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Generate Comments
                </button>

                {generatedComments.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">2</div>
                      <span className="text-sm font-medium text-foreground">Choose & Post</span>
                      <span className="ml-auto text-xs text-emerald-500 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Click to copy & open LinkedIn
                      </span>
                    </div>
                    {generatedComments.map((item, i) => (
                      <div key={i} className="bg-card-hover rounded-lg p-4 hover:bg-card-hover/80 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-amber-500/10 text-amber-500 rounded-full mb-2">{item.style}</span>
                            <p className="text-foreground text-sm leading-relaxed">{item.comment}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => copyComment(item.comment, `comment-${i}`)} className="p-2 text-muted hover:text-amber-500 transition-colors" title="Copy comment">
                              {copied === `comment-${i}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                            </button>
                            <button onClick={() => postCommentToLinkedIn(item.comment, i)} className="p-2 rounded-lg transition-all flex items-center gap-1 bg-[#0077b5] text-white hover:bg-[#006097]" title="Copy & Open LinkedIn Post">
                              {copied === `linkedin-${i}` ? (
                                <><Check className="w-4 h-4" /><span className="text-xs">Copied!</span></>
                              ) : (
                                <><Linkedin className="w-4 h-4" /><span className="text-xs">Post</span></>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-muted text-center py-2">
                      Click "Post" to copy the comment and open LinkedIn — then paste and submit
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* URL Import Modal */}
      <AnimatePresence>
        {showUrlImport && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUrlImport(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-card border border-border rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Link2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground">Import from URL</h2>
                  <p className="text-muted text-sm">Create a post inspired by external content</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">URL</label>
                <input type="url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://linkedin.com/posts/... or any article URL" className="input w-full" />
                <p className="text-xs text-muted mt-2">Supports LinkedIn posts, articles, tweets, blog posts, and news articles.</p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowUrlImport(false)} className="btn-secondary">Cancel</button>
                <button onClick={importFromUrl} disabled={urlLoading || !urlInput.trim()} className="btn-primary flex items-center gap-2">
                  {urlLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  Import & Create
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiz Templates Modal */}
      <AnimatePresence>
        {showQuizTemplates && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowQuizTemplates(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">Quiz Templates</h2>
                    <p className="text-muted text-sm">Choose a template to create your quiz</p>
                  </div>
                </div>
                <button onClick={() => setShowQuizTemplates(false)} className="p-2 text-muted hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex gap-2 px-6 pt-4 flex-wrap">
                {['All', ...new Set(templates.map(t => t.category))].map((cat) => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)} className={cn("px-3 py-1.5 rounded-full text-sm font-medium transition-colors", selectedCategory === cat ? "bg-emerald-500 text-white" : "bg-card-hover text-muted hover:text-foreground")}>{cat}</button>
                ))}
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[55vh]">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredTemplates.map((template) => {
                    const IconComponent = templateIcons[template.icon] || Briefcase;
                    return (
                      <motion.button key={template.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => handleQuizTemplateSelect(template)} className="card text-left hover:border-emerald-500/50 transition-all p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-emerald-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-foreground text-sm">{template.name}</h3>
                            <p className="text-xs text-muted mt-1 line-clamp-2">{template.description}</p>
                            <div className="flex gap-1 mt-2">
                              <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-xs text-emerald-500">Quiz</span>
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
